import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
    Book, LayoutDashboard, Users, Package, Scissors, Wrench, Settings, UserCog,
    Search, ChevronDown, ChevronRight, Database, Sparkles, ShieldCheck,
    FileText, ShoppingBag, CheckSquare, AlertTriangle, Wallet, Receipt, FileSpreadsheet, X, History,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import VersionLabel from "./VersionLabel";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveLogo = (p) => {
    if (!p) return "";
    if (p.startsWith("/api/") || p.startsWith("/uploads/")) return `${BACKEND}${p}`;
    return p;
};

export default function Sidebar({ open, onClose }) {
    const [vendors, setVendors] = useState([]);
    const [query, setQuery] = useState("");
    const [logoUrl, setLogoUrl] = useState("");
    const [namaPerusahaan, setNamaPerusahaan] = useState("");

    useEffect(() => {
        api.get("/pengaturan/public").then((r) => {
            setLogoUrl(r.data?.logo_url || "");
            setNamaPerusahaan(r.data?.nama_perusahaan || "");
        }).catch(() => { /* public */ });
    }, []);

    const [openSection, setOpenSection] = useState({ vendor: true, transaksi: true, data: true, sistem: true });
    const loc = useLocation();
    const { user } = useAuth();

    useEffect(() => {
        const load = () => api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [loc.pathname]);

    const filtered = query
        ? vendors.filter((v) =>
            v.nama.toLowerCase().includes(query.toLowerCase()) ||
            (v.kode_vendor || "").toLowerCase().includes(query.toLowerCase()))
        : vendors;
    const currentVendorId = loc.pathname.startsWith("/vendor/") ? loc.pathname.split("/")[2] : null;

    const linkCls = ({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
            isActive
                ? "bg-[var(--apple-blue-soft)] text-[var(--apple-blue)] font-medium"
                : "text-[var(--text-primary)] hover:bg-[var(--bg-subtle)]"
        }`;

    const subLinkCls = ({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[13px] transition-colors ${
            isActive
                ? "bg-[var(--apple-blue-soft)] text-[var(--apple-blue)] font-medium"
                : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-subtle)]"
        }`;

    const SectionHeader = ({ title, k }) => (
        <button
            onClick={() => setOpenSection((s) => ({ ...s, [k]: !s[k] }))}
            className="w-full flex items-center justify-between px-3 pt-4 pb-1 text-left"
        >
            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                {title}
            </span>
            {openSection[k] ? (
                <ChevronDown className="w-3 h-3" style={{ color: "var(--text-muted)" }} />
            ) : (
                <ChevronRight className="w-3 h-3" style={{ color: "var(--text-muted)" }} />
            )}
        </button>
    );

    return (
        <>
            {open && <div className="fixed inset-0 z-30 sidebar-overlay-mobile md:hidden" onClick={onClose} />}
            <aside
                data-testid="app-sidebar"
                className={`fixed md:static z-40 inset-y-0 left-0 flex flex-col transform transition-transform ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
                style={{
                    width: "var(--sidebar-w)",
                    background: "var(--bg-sidebar)",
                    color: "var(--text-primary)",
                    borderRight: "1px solid var(--border)",
                }}
            >
                {/* Brand header */}
                <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: "var(--border)" }}>
                    {logoUrl ? (
                        <img
                            src={resolveLogo(logoUrl)}
                            alt="logo"
                            className="w-8 h-8 rounded-md object-contain flex-shrink-0"
                            data-testid="sidebar-logo"
                            onError={() => setLogoUrl("")}
                        />
                    ) : (
                        <div
                            className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                            style={{ background: "var(--apple-blue-soft)", color: "var(--apple-blue)" }}
                            data-testid="sidebar-logo-placeholder"
                        >
                            <Book className="w-4 h-4" />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate" style={{ color: "var(--text-primary)" }}>
                            {namaPerusahaan || "CV Meranggi Jaya"}
                        </div>
                        <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                            Buku Vendor Garment
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="md:hidden p-1 rounded hover:bg-[var(--bg-subtle)]"
                        aria-label="Tutup menu"
                        data-testid="sidebar-close"
                    >
                        <X className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
                    </button>
                </div>

                <nav className="flex-1 overflow-y-auto py-2">
                    <div className="px-2">
                        <NavLink to="/dashboard" className={linkCls} data-testid="sidebar-dashboard" onClick={onClose}>
                            <LayoutDashboard className="w-4 h-4" />
                            Dashboard Master
                        </NavLink>
                    </div>

                    {/* Transaksi — compact list */}
                    <SectionHeader title="Transaksi" k="transaksi" />
                    {openSection.transaksi && (
                        <div className="px-2 space-y-0.5">
                            <NavLink to="/spk" className={subLinkCls} data-testid="sidebar-spk" onClick={onClose}>
                                <FileText className="w-3.5 h-3.5" /> SPK / Job Order
                            </NavLink>
                            <NavLink to="/bon-kain" className={subLinkCls} data-testid="sidebar-bon-kain" onClick={onClose}>
                                <ShoppingBag className="w-3.5 h-3.5" /> Bon Kain
                            </NavLink>
                            <NavLink to="/bon-peralatan" className={subLinkCls} data-testid="sidebar-bon-peralatan" onClick={onClose}>
                                <Wrench className="w-3.5 h-3.5" /> Bon Peralatan
                            </NavLink>
                            <NavLink to="/setor-barang" className={subLinkCls} data-testid="sidebar-setor" onClick={onClose}>
                                <Package className="w-3.5 h-3.5" /> Setor Barang
                            </NavLink>
                            <NavLink to="/qc" className={subLinkCls} data-testid="sidebar-qc" onClick={onClose}>
                                <CheckSquare className="w-3.5 h-3.5" /> QC
                            </NavLink>
                            <NavLink to="/reject-perbaikan" className={subLinkCls} data-testid="sidebar-reject" onClick={onClose}>
                                <AlertTriangle className="w-3.5 h-3.5" /> Reject
                            </NavLink>
                            <NavLink to="/kasbon" className={subLinkCls} data-testid="sidebar-kasbon" onClick={onClose}>
                                <Wallet className="w-3.5 h-3.5" /> Kasbon
                            </NavLink>
                            <NavLink to="/invoice" className={subLinkCls} data-testid="sidebar-invoice" onClick={onClose}>
                                <Receipt className="w-3.5 h-3.5" /> Invoice
                            </NavLink>
                            <NavLink to="/laporan" className={subLinkCls} data-testid="sidebar-laporan" onClick={onClose}>
                                <FileSpreadsheet className="w-3.5 h-3.5" /> Laporan / Export
                            </NavLink>
                        </div>
                    )}

                    {/* Vendor list */}
                    <SectionHeader title="Vendor" k="vendor" />
                    {openSection.vendor && (
                        <div className="px-2 space-y-0.5">
                            <div className="relative mb-1 mx-1">
                                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Cari vendor..."
                                    data-testid="sidebar-vendor-search"
                                    className="w-full text-xs rounded-lg pl-8 pr-2 py-1.5"
                                    style={{ background: "#fff", border: "1px solid var(--border)", color: "var(--text-primary)" }}
                                />
                            </div>
                            {filtered.length === 0 && (
                                <div data-testid="sidebar-vendor-empty" className="px-3 py-2 text-[11px] italic" style={{ color: "var(--text-muted)" }}>
                                    Belum ada vendor
                                </div>
                            )}
                            {filtered.slice(0, 50).map((v) => (
                                <NavLink
                                    key={v.id}
                                    to={`/vendor/${v.id}`}
                                    data-testid={`sidebar-vendor-${v.id}`}
                                    className={({ isActive }) =>
                                        `flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] transition-colors ${
                                            (isActive || currentVendorId === v.id)
                                                ? "bg-[var(--apple-blue-soft)] text-[var(--apple-blue)] font-medium"
                                                : "text-[var(--text-primary)] hover:bg-[var(--bg-subtle)]"
                                        }`
                                    }
                                    onClick={onClose}
                                >
                                    <span className="truncate flex-1">{v.nama}</span>
                                    <span className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>
                                        {v.kode_vendor}
                                    </span>
                                </NavLink>
                            ))}
                        </div>
                    )}

                    {/* Data Input */}
                    <SectionHeader title="Data Input" k="data" />
                    {openSection.data && (
                        <div className="px-2 space-y-0.5">
                            <NavLink to="/vendor" className={subLinkCls} data-testid="sidebar-data-vendor" onClick={onClose}>
                                <Users className="w-3.5 h-3.5" /> Vendor
                            </NavLink>
                            <NavLink to="/produk" className={subLinkCls} data-testid="sidebar-data-produk" onClick={onClose}>
                                <Package className="w-3.5 h-3.5" /> Produk & Harga
                            </NavLink>
                            <NavLink to="/jenis-kain" className={subLinkCls} data-testid="sidebar-data-kain" onClick={onClose}>
                                <Scissors className="w-3.5 h-3.5" /> Kain
                            </NavLink>
                            <NavLink to="/peralatan" className={subLinkCls} data-testid="sidebar-data-peralatan" onClick={onClose}>
                                <Wrench className="w-3.5 h-3.5" /> Bahan Penolong
                            </NavLink>
                        </div>
                    )}

                    {/* Pengaturan */}
                    <SectionHeader title="Pengaturan" k="sistem" />
                    {openSection.sistem && (
                        <div className="px-2 space-y-0.5">
                            <NavLink to="/pengaturan" className={subLinkCls} data-testid="sidebar-pengaturan" onClick={onClose}>
                                <Settings className="w-3.5 h-3.5" /> Profil Perusahaan
                            </NavLink>
                            <NavLink to="/konfigurasi-ai" className={subLinkCls} data-testid="sidebar-konfigurasi-ai" onClick={onClose}>
                                <Sparkles className="w-3.5 h-3.5" /> Konfigurasi AI
                            </NavLink>
                            <NavLink to="/backup-data" className={subLinkCls} data-testid="sidebar-backup-data" onClick={onClose}>
                                <Database className="w-3.5 h-3.5" /> Backup Data
                            </NavLink>
                            <NavLink to="/riwayat-terhapus" className={subLinkCls} data-testid="sidebar-riwayat-terhapus" onClick={onClose}>
                                <History className="w-3.5 h-3.5" /> Riwayat Terhapus
                            </NavLink>
                            {user?.is_owner && (
                                <NavLink to="/account-center" className={subLinkCls} data-testid="sidebar-account-center" onClick={onClose}>
                                    <ShieldCheck className="w-3.5 h-3.5" /> Account Center
                                </NavLink>
                            )}
                            {user?.is_owner && (
                                <NavLink to="/users" className={subLinkCls} data-testid="sidebar-users" onClick={onClose}>
                                    <UserCog className="w-3.5 h-3.5" /> Petugas (legacy)
                                </NavLink>
                            )}
                        </div>
                    )}
                </nav>

                <VersionLabel />
            </aside>
        </>
    );
}
